import { useState, useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";
import { View, StyleSheet, ScrollView, Pressable, useWindowDimensions } from "react-native";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Avatar from "@/components/Avatar";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { router, usePathname, useGlobalSearchParams } from "expo-router";
import { useUpdate } from "@/providers/UpdateProvider";
import { hardReload } from "@/lib/reload";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getAllSaveBehaviors, setSaveBehavior, SaveBehavior, EntityType } from "@/lib/saveSettings";
import {
  QuickSettingsSection,
  SaveBehaviorDropdown,
  ConfirmationToggle,
  CopyOptionsChips,
  CopyOption,
  ToastLocationPicker,
} from "./QuickSettings";

export interface ProfileMenuProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const DRAWER_WIDTH = 400;
const MIN_BACKDROP_WIDTH = 80; // Always leave at least 80px for backdrop to close

export function ProfileMenu({ visible, setVisible }: ProfileMenuProps) {
  const { colors } = useTheme();
  const { user, displayNames } = useAuth();
  const { signOut, session } = useAuth();
  const permissions = usePermissions();
  const { width: windowWidth } = useWindowDimensions();
  const pathname = usePathname();
  const params = useGlobalSearchParams();
  // Version comes from the UpdateProvider — the version it actually reconciled
  // against the remote /api/v1/version, so the badge shows the true running build
  // and flags when a newer one is live.
  const { currentVersion, latestVersion, updateAvailable } = useUpdate();

  // Drawer is 400px unless viewport is too narrow, then take remaining space
  const drawerWidth = windowWidth > DRAWER_WIDTH + MIN_BACKDROP_WIDTH ? DRAWER_WIDTH : windowWidth - MIN_BACKDROP_WIDTH;

  // Permission checks - derived from usePermissions hook
  const canUpdateTimecards = permissions.time_cards.update;
  const canUpdateUsers = permissions.users.update;
  const canUpdateProjects = permissions.projects.update;
  const canUpdateContacts = permissions.contacts.update;
  const canUpdateInvoices = permissions.invoices.update;
  const canUpdateEstimates = permissions.estimates.update;
  const canUpdateExpenses = permissions.expenses.update;
  const canUpdateApplications = permissions.applications.update;
  const canUpdateCourses = permissions.courses.update;
  const canUpdateIncidents = permissions.incidents.update;
  const canUpdateInvestigations = permissions.investigations.update;
  const canUpdatePayroll = permissions.payroll.update;
  const canUpdateClients = permissions.clients.update;

  // Create permission checks for save behavior settings
  const canCreateClients = permissions.clients.create;
  const canCreateContacts = permissions.contacts.create;
  const canCreateCourses = permissions.courses.create;
  const canCreateEstimates = permissions.estimates.create;
  const canCreateIncidents = permissions.incidents.create;
  const canCreateInvoices = permissions.invoices.create;
  const canCreateProjects = permissions.projects.create;
  const canCreateShifts = permissions.shifts.create;
  const canCreateTimecards = permissions.time_cards.create;
  const canCreateApplications = permissions.applications.create;
  const canCreateUsers = permissions.users.create;

  // Timecard quick settings
  const [copyOption, setCopyOption] = useState<CopyOption>("none");
  const [showTimecardConfirmation, setShowTimecardConfirmation] = useState(true);
  const [showTimecardDeleteConfirmation, setShowTimecardDeleteConfirmation] = useState(true);

  // User quick settings
  const [showUserDeactivateConfirmation, setShowUserDeactivateConfirmation] = useState(true);
  const [showUserDeleteConfirmation, setShowUserDeleteConfirmation] = useState(true);

  // Delete confirmation settings for all other entities
  const [showProjectDeleteConfirmation, setShowProjectDeleteConfirmation] = useState(true);
  const [showContactDeleteConfirmation, setShowContactDeleteConfirmation] = useState(true);
  const [showInvoiceDeleteConfirmation, setShowInvoiceDeleteConfirmation] = useState(true);
  const [showBillingItemDeleteConfirmation, setShowBillingItemDeleteConfirmation] = useState(true);
  const [showEstimateDeleteConfirmation, setShowEstimateDeleteConfirmation] = useState(true);
  const [showExpenseDeleteConfirmation, setShowExpenseDeleteConfirmation] = useState(true);
  const [showApplicationDeleteConfirmation, setShowApplicationDeleteConfirmation] = useState(true);
  const [showCourseDeleteConfirmation, setShowCourseDeleteConfirmation] = useState(true);
  const [showIncidentDeleteConfirmation, setShowIncidentDeleteConfirmation] = useState(true);
  const [showInvestigationDeleteConfirmation, setShowInvestigationDeleteConfirmation] = useState(true);
  const [showPayrollDeleteConfirmation, setShowPayrollDeleteConfirmation] = useState(true);
  const [showPayrollItemDeleteConfirmation, setShowPayrollItemDeleteConfirmation] = useState(true);
  const [showClientDeleteConfirmation, setShowClientDeleteConfirmation] = useState(true);

  // Save behavior settings
  const [saveBehaviors, setSaveBehaviors] = useState<Record<EntityType, SaveBehavior>>(
    {} as Record<EntityType, SaveBehavior>,
  );
  const [activeSaveBehaviorDropdown, setActiveSaveBehaviorDropdown] = useState<EntityType | null>(null);

  // Reload settings when menu becomes visible
  useEffect(() => {
    if (!visible) return;

    const loadSettings = async () => {
      const [
        savedCopyOption,
        timecardSkip,
        userDeactivateSkip,
        timecardDeleteSkip,
        userDeleteSkip,
        projectDeleteSkip,
        contactDeleteSkip,
        invoiceDeleteSkip,
        billingItemDeleteSkip,
        estimateDeleteSkip,
        expenseDeleteSkip,
        applicationDeleteSkip,
        courseDeleteSkip,
        incidentDeleteSkip,
        investigationDeleteSkip,
        payrollDeleteSkip,
        payrollItemDeleteSkip,
        clientDeleteSkip,
      ] = await Promise.all([
        AsyncStorage.getItem("timecard_approve_default_copy"),
        AsyncStorage.getItem("timecard_approve_skip_confirmation"),
        AsyncStorage.getItem("user_deactivate_skip_confirmation"),
        AsyncStorage.getItem("timecard_delete_skip_confirmation"),
        AsyncStorage.getItem("user_delete_skip_confirmation"),
        AsyncStorage.getItem("project_delete_skip_confirmation"),
        AsyncStorage.getItem("contact_delete_skip_confirmation"),
        AsyncStorage.getItem("invoice_delete_skip_confirmation"),
        AsyncStorage.getItem("billing_item_delete_skip_confirmation"),
        AsyncStorage.getItem("estimate_delete_skip_confirmation"),
        AsyncStorage.getItem("expense_delete_skip_confirmation"),
        AsyncStorage.getItem("application_delete_skip_confirmation"),
        AsyncStorage.getItem("course_delete_skip_confirmation"),
        AsyncStorage.getItem("incident_delete_skip_confirmation"),
        AsyncStorage.getItem("investigation_delete_skip_confirmation"),
        AsyncStorage.getItem("payroll_delete_skip_confirmation"),
        AsyncStorage.getItem("payroll_item_delete_skip_confirmation"),
        AsyncStorage.getItem("client_delete_skip_confirmation"),
      ]);
      if (savedCopyOption) setCopyOption(savedCopyOption as CopyOption);
      setShowTimecardConfirmation(timecardSkip !== "true");
      setShowUserDeactivateConfirmation(userDeactivateSkip !== "true");
      setShowTimecardDeleteConfirmation(timecardDeleteSkip !== "true");
      setShowUserDeleteConfirmation(userDeleteSkip !== "true");
      setShowProjectDeleteConfirmation(projectDeleteSkip !== "true");
      setShowContactDeleteConfirmation(contactDeleteSkip !== "true");
      setShowInvoiceDeleteConfirmation(invoiceDeleteSkip !== "true");
      setShowBillingItemDeleteConfirmation(billingItemDeleteSkip !== "true");
      setShowEstimateDeleteConfirmation(estimateDeleteSkip !== "true");
      setShowExpenseDeleteConfirmation(expenseDeleteSkip !== "true");
      setShowApplicationDeleteConfirmation(applicationDeleteSkip !== "true");
      setShowCourseDeleteConfirmation(courseDeleteSkip !== "true");
      setShowIncidentDeleteConfirmation(incidentDeleteSkip !== "true");
      setShowInvestigationDeleteConfirmation(investigationDeleteSkip !== "true");
      setShowPayrollDeleteConfirmation(payrollDeleteSkip !== "true");
      setShowPayrollItemDeleteConfirmation(payrollItemDeleteSkip !== "true");
      setShowClientDeleteConfirmation(clientDeleteSkip !== "true");

      // Load save behaviors
      const behaviors = await getAllSaveBehaviors();
      setSaveBehaviors(behaviors);
    };
    loadSettings();
  }, [visible]);

  const handleSaveBehaviorChange = async (entity: EntityType, behavior: SaveBehavior) => {
    await setSaveBehavior(entity, behavior);
    setSaveBehaviors((prev) => ({ ...prev, [entity]: behavior }));
  };

  const handleCopyOptionChange = async (option: CopyOption) => {
    setCopyOption(option);
    await AsyncStorage.setItem("timecard_approve_default_copy", option);
  };

  // Toggle handlers for confirmation settings
  const createToggleHandler = (key: string, currentValue: boolean, setter: (value: boolean) => void) => async () => {
    const newValue = !currentValue;
    setter(newValue);
    await AsyncStorage.setItem(key, newValue ? "false" : "true");
  };

  const handleTimecardConfirmationToggle = createToggleHandler(
    "timecard_approve_skip_confirmation",
    showTimecardConfirmation,
    setShowTimecardConfirmation,
  );
  const handleUserDeactivateConfirmationToggle = createToggleHandler(
    "user_deactivate_skip_confirmation",
    showUserDeactivateConfirmation,
    setShowUserDeactivateConfirmation,
  );
  const handleTimecardDeleteConfirmationToggle = createToggleHandler(
    "timecard_delete_skip_confirmation",
    showTimecardDeleteConfirmation,
    setShowTimecardDeleteConfirmation,
  );
  const handleUserDeleteConfirmationToggle = createToggleHandler(
    "user_delete_skip_confirmation",
    showUserDeleteConfirmation,
    setShowUserDeleteConfirmation,
  );
  const handleProjectDeleteConfirmationToggle = createToggleHandler(
    "project_delete_skip_confirmation",
    showProjectDeleteConfirmation,
    setShowProjectDeleteConfirmation,
  );
  const handleContactDeleteConfirmationToggle = createToggleHandler(
    "contact_delete_skip_confirmation",
    showContactDeleteConfirmation,
    setShowContactDeleteConfirmation,
  );
  const handleInvoiceDeleteConfirmationToggle = createToggleHandler(
    "invoice_delete_skip_confirmation",
    showInvoiceDeleteConfirmation,
    setShowInvoiceDeleteConfirmation,
  );
  const handleBillingItemDeleteConfirmationToggle = createToggleHandler(
    "billing_item_delete_skip_confirmation",
    showBillingItemDeleteConfirmation,
    setShowBillingItemDeleteConfirmation,
  );
  const handleEstimateDeleteConfirmationToggle = createToggleHandler(
    "estimate_delete_skip_confirmation",
    showEstimateDeleteConfirmation,
    setShowEstimateDeleteConfirmation,
  );
  const handleExpenseDeleteConfirmationToggle = createToggleHandler(
    "expense_delete_skip_confirmation",
    showExpenseDeleteConfirmation,
    setShowExpenseDeleteConfirmation,
  );
  const handleApplicationDeleteConfirmationToggle = createToggleHandler(
    "application_delete_skip_confirmation",
    showApplicationDeleteConfirmation,
    setShowApplicationDeleteConfirmation,
  );
  const handleCourseDeleteConfirmationToggle = createToggleHandler(
    "course_delete_skip_confirmation",
    showCourseDeleteConfirmation,
    setShowCourseDeleteConfirmation,
  );
  const handleIncidentDeleteConfirmationToggle = createToggleHandler(
    "incident_delete_skip_confirmation",
    showIncidentDeleteConfirmation,
    setShowIncidentDeleteConfirmation,
  );
  const handleInvestigationDeleteConfirmationToggle = createToggleHandler(
    "investigation_delete_skip_confirmation",
    showInvestigationDeleteConfirmation,
    setShowInvestigationDeleteConfirmation,
  );
  const handlePayrollDeleteConfirmationToggle = createToggleHandler(
    "payroll_delete_skip_confirmation",
    showPayrollDeleteConfirmation,
    setShowPayrollDeleteConfirmation,
  );
  const handlePayrollItemDeleteConfirmationToggle = createToggleHandler(
    "payroll_item_delete_skip_confirmation",
    showPayrollItemDeleteConfirmation,
    setShowPayrollItemDeleteConfirmation,
  );
  const handleClientDeleteConfirmationToggle = createToggleHandler(
    "client_delete_skip_confirmation",
    showClientDeleteConfirmation,
    setShowClientDeleteConfirmation,
  );

  // Display names
  const timecardDisplayName = displayNames?.time_cards?.plural || "Timecards";
  const userDisplayName = displayNames?.users?.plural || "Users";
  const projectDisplayName = displayNames?.projects?.plural || "Projects";
  const contactDisplayName = displayNames?.contacts?.plural || "Contacts";
  const invoiceDisplayName = displayNames?.invoices?.plural || "Invoices";
  const estimateDisplayName = displayNames?.estimates?.plural || "Estimates";
  const expenseDisplayName = displayNames?.expenses?.plural || "Expenses";
  const applicationDisplayName = displayNames?.applications?.plural || "Applications";
  const courseDisplayName = displayNames?.courses?.plural || "Courses";
  const incidentDisplayName = displayNames?.incidents?.plural || "Incidents";
  const investigationDisplayName = displayNames?.investigations?.plural || "Investigations";
  const payrollDisplayName = displayNames?.payroll?.plural || "Payroll";
  const clientDisplayName = displayNames?.clients?.plural || "Clients";

  const handleClose = () => setVisible(false);

  if (!visible) return null;

  return (
    <View style={[StyleSheet.absoluteFill, styles.overlay]}>
      <Pressable style={styles.backdrop} onPress={handleClose} />
      <View
        style={[
          styles.drawer,
          { width: drawerWidth, backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
        ]}
      >
        <View style={{ padding: 16, gap: 16 }}>
          <View style={styles.header}>
            <Avatar userId={user?.id} />
            <ThemedText style={{ color: colors.text, padding: 8 }}>{`${session!.user.email}`}</ThemedText>
            <Button style={[styles.closeButton]} icon="close" onPress={handleClose} />
          </View>
          <View style={styles.opts}>
            <Button
              iconPosition="right"
              style={styles.button}
              text="Settings"
              icon="cog"
              onPress={() => {
                handleClose();
                // Capture current location as referer for settings back button
                const referer = JSON.stringify({ pathname, params });
                router.push({ pathname: "/settings/user", params: { referer } });
              }}
            />
            <Button
              iconPosition="right"
              style={styles.button}
              text="Sign out"
              icon="logout"
              onPress={() => signOut()}
            />
            <Button
              iconPosition="right"
              style={styles.button}
              text="Change Password"
              icon="key-change"
              onPress={() => {
                handleClose();
                router.push("/update-password");
              }}
            />
          </View>

          <ScrollView persistentScrollbar showsVerticalScrollIndicator style={styles.scrollContainer}>
            {/* Timecard Quick Settings */}
            {(canUpdateTimecards || canCreateTimecards) && (
              <QuickSettingsSection
                title={timecardDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "timecard"}
              >
                {canUpdateTimecards && (
                  <>
                    <CopyOptionsChips selectedOption={copyOption} onOptionChange={handleCopyOptionChange} />
                    <ConfirmationToggle
                      label="Show when approving"
                      checked={showTimecardConfirmation}
                      onToggle={handleTimecardConfirmationToggle}
                    />
                    <ConfirmationToggle
                      label="Show when deleting"
                      checked={showTimecardDeleteConfirmation}
                      onToggle={handleTimecardDeleteConfirmationToggle}
                    />
                  </>
                )}
                {canCreateTimecards && (
                  <SaveBehaviorDropdown
                    entity="timecard"
                    currentBehavior={saveBehaviors.timecard || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "timecard"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "timecard" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* User Quick Settings */}
            {(canUpdateUsers || canCreateUsers) && (
              <QuickSettingsSection title={userDisplayName} hasOpenDropdown={activeSaveBehaviorDropdown === "user"}>
                {canUpdateUsers && (
                  <>
                    <ConfirmationToggle
                      label="Show when deactivating"
                      checked={showUserDeactivateConfirmation}
                      onToggle={handleUserDeactivateConfirmationToggle}
                    />
                    <ConfirmationToggle
                      label="Show when deleting"
                      checked={showUserDeleteConfirmation}
                      onToggle={handleUserDeleteConfirmationToggle}
                    />
                  </>
                )}
                {canCreateUsers && (
                  <SaveBehaviorDropdown
                    entity="user"
                    currentBehavior={saveBehaviors.user || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "user"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "user" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Project Quick Settings */}
            {(canUpdateProjects || canCreateProjects) && (
              <QuickSettingsSection
                title={projectDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "project"}
              >
                {canUpdateProjects && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showProjectDeleteConfirmation}
                    onToggle={handleProjectDeleteConfirmationToggle}
                  />
                )}
                {canCreateProjects && (
                  <SaveBehaviorDropdown
                    entity="project"
                    currentBehavior={saveBehaviors.project || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "project"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "project" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Shift Quick Settings */}
            {canCreateShifts && (
              <QuickSettingsSection title="Shifts" hasOpenDropdown={activeSaveBehaviorDropdown === "shift"}>
                <SaveBehaviorDropdown
                  entity="shift"
                  currentBehavior={saveBehaviors.shift || "close"}
                  onBehaviorChange={handleSaveBehaviorChange}
                  isOpen={activeSaveBehaviorDropdown === "shift"}
                  onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "shift" : null)}
                />
              </QuickSettingsSection>
            )}

            {/* Contact Quick Settings */}
            {(canUpdateContacts || canCreateContacts) && (
              <QuickSettingsSection
                title={contactDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "contact"}
              >
                {canUpdateContacts && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showContactDeleteConfirmation}
                    onToggle={handleContactDeleteConfirmationToggle}
                  />
                )}
                {canCreateContacts && (
                  <SaveBehaviorDropdown
                    entity="contact"
                    currentBehavior={saveBehaviors.contact || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "contact"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "contact" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Client Quick Settings */}
            {(canUpdateClients || canCreateClients) && (
              <QuickSettingsSection title={clientDisplayName} hasOpenDropdown={activeSaveBehaviorDropdown === "client"}>
                {canUpdateClients && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showClientDeleteConfirmation}
                    onToggle={handleClientDeleteConfirmationToggle}
                  />
                )}
                {canCreateClients && (
                  <SaveBehaviorDropdown
                    entity="client"
                    currentBehavior={saveBehaviors.client || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "client"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "client" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Invoice Quick Settings */}
            {(canUpdateInvoices || canCreateInvoices) && (
              <QuickSettingsSection
                title={invoiceDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "invoice"}
              >
                {canUpdateInvoices && (
                  <>
                    <ConfirmationToggle
                      label="Show when deleting"
                      checked={showInvoiceDeleteConfirmation}
                      onToggle={handleInvoiceDeleteConfirmationToggle}
                    />
                    <ConfirmationToggle
                      label="Show when deleting billing items"
                      checked={showBillingItemDeleteConfirmation}
                      onToggle={handleBillingItemDeleteConfirmationToggle}
                    />
                  </>
                )}
                {canCreateInvoices && (
                  <SaveBehaviorDropdown
                    entity="invoice"
                    currentBehavior={saveBehaviors.invoice || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "invoice"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "invoice" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Estimate Quick Settings */}
            {(canUpdateEstimates || canCreateEstimates) && (
              <QuickSettingsSection
                title={estimateDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "estimate"}
              >
                {canUpdateEstimates && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showEstimateDeleteConfirmation}
                    onToggle={handleEstimateDeleteConfirmationToggle}
                  />
                )}
                {canCreateEstimates && (
                  <SaveBehaviorDropdown
                    entity="estimate"
                    currentBehavior={saveBehaviors.estimate || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "estimate"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "estimate" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Expense Quick Settings */}
            {canUpdateExpenses && (
              <QuickSettingsSection title={expenseDisplayName}>
                <ConfirmationToggle
                  label="Show when deleting"
                  checked={showExpenseDeleteConfirmation}
                  onToggle={handleExpenseDeleteConfirmationToggle}
                />
              </QuickSettingsSection>
            )}

            {/* Application Quick Settings */}
            {(canUpdateApplications || canCreateApplications) && (
              <QuickSettingsSection
                title={applicationDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "application"}
              >
                {canUpdateApplications && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showApplicationDeleteConfirmation}
                    onToggle={handleApplicationDeleteConfirmationToggle}
                  />
                )}
                {canCreateApplications && (
                  <SaveBehaviorDropdown
                    entity="application"
                    currentBehavior={saveBehaviors.application || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "application"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "application" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Course Quick Settings */}
            {(canUpdateCourses || canCreateCourses) && (
              <QuickSettingsSection title={courseDisplayName} hasOpenDropdown={activeSaveBehaviorDropdown === "course"}>
                {canUpdateCourses && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showCourseDeleteConfirmation}
                    onToggle={handleCourseDeleteConfirmationToggle}
                  />
                )}
                {canCreateCourses && (
                  <SaveBehaviorDropdown
                    entity="course"
                    currentBehavior={saveBehaviors.course || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "course"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "course" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Incident Quick Settings */}
            {(canUpdateIncidents || canCreateIncidents) && (
              <QuickSettingsSection
                title={incidentDisplayName}
                hasOpenDropdown={activeSaveBehaviorDropdown === "incident"}
              >
                {canUpdateIncidents && (
                  <ConfirmationToggle
                    label="Show when deleting"
                    checked={showIncidentDeleteConfirmation}
                    onToggle={handleIncidentDeleteConfirmationToggle}
                  />
                )}
                {canCreateIncidents && (
                  <SaveBehaviorDropdown
                    entity="incident"
                    currentBehavior={saveBehaviors.incident || "close"}
                    onBehaviorChange={handleSaveBehaviorChange}
                    isOpen={activeSaveBehaviorDropdown === "incident"}
                    onOpenChange={(isOpen) => setActiveSaveBehaviorDropdown(isOpen ? "incident" : null)}
                  />
                )}
              </QuickSettingsSection>
            )}

            {/* Investigation Quick Settings */}
            {canUpdateInvestigations && (
              <QuickSettingsSection title={investigationDisplayName}>
                <ConfirmationToggle
                  label="Show when deleting"
                  checked={showInvestigationDeleteConfirmation}
                  onToggle={handleInvestigationDeleteConfirmationToggle}
                />
              </QuickSettingsSection>
            )}

            {/* Payroll Quick Settings */}
            {canUpdatePayroll && (
              <QuickSettingsSection title={payrollDisplayName}>
                <ConfirmationToggle
                  label="Show when deleting"
                  checked={showPayrollDeleteConfirmation}
                  onToggle={handlePayrollDeleteConfirmationToggle}
                />
                <ConfirmationToggle
                  label="Show when deleting payroll items"
                  checked={showPayrollItemDeleteConfirmation}
                  onToggle={handlePayrollItemDeleteConfirmationToggle}
                />
              </QuickSettingsSection>
            )}
          </ScrollView>

          {/* Toast Location */}
          <ToastLocationPicker />
        </View>
        <View style={[styles.versionBadge, { backgroundColor: colors.backgroundSecondary }]}>
          <ThemedText style={[styles.versionLabel, { color: colors.textSecondary }]}>version</ThemedText>
          <ThemedText style={[styles.versionNumber, { color: colors.text }]}>{currentVersion}</ThemedText>
          {updateAvailable && (
            <>
              <ThemedText style={[styles.versionUpdate, { color: colors.primary }]}>↑ {latestVersion}</ThemedText>
              <Pressable
                onPress={() => hardReload()}
                hitSlop={6}
                style={({ pressed, hovered }) => [
                  styles.versionRefresh,
                  {
                    backgroundColor: colors.green.background,
                    borderColor: colors.green.border,
                    cursor: "pointer",
                  } as any,
                  hovered && { opacity: 0.85 },
                  pressed && { opacity: 0.7 },
                ]}
              >
                <MaterialCommunityIcons name="refresh" size={12} color="#ffffff" />
                <ThemedText style={styles.versionRefreshText}>Refresh</ThemedText>
              </Pressable>
            </>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: "fixed",
    zIndex: 2,
    elevation: 2,
    flexDirection: "row",
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  drawer: {
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  opts: {
    gap: 8,
  },
  scrollContainer: {
    maxHeight: 400,
  },
  closeButton: {
    borderWidth: 0,
    backgroundColor: "transparent",
    width: 30,
    height: 30,
  },
  button: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderWidth: 0,
    justifyContent: "space-between",
  },
  versionBadge: {
    flexDirection: "row",
    alignItems: "flex-end",
    alignSelf: "flex-end",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  versionLabel: {
    fontSize: 12,
  },
  versionNumber: {
    fontSize: 13,
    fontWeight: "600",
  },
  versionUpdate: {
    fontSize: 12,
    fontWeight: "600",
  },
  versionRefresh: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    marginLeft: 4,
  },
  versionRefreshText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#ffffff",
  },
});
